import {
  Component, OnInit, OnDestroy, DestroyRef, inject, ViewChild, ElementRef,
  ChangeDetectionStrategy, ChangeDetectorRef, HostListener
} from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';

import { MeetingNotesPanelService } from '../services/meeting-notes-panel.service';
import { MeetingNotesService } from '../services/meeting-notes.service';
import { FileStorageService } from '../../../file-storage/services/file-storage.service';
import { TimeSheetService } from '../../../time-sheet/services/time-sheet.service';
import { ITimeSheetRequest } from '../../../time-sheet/models/ITimeSheetRequest.interface';
import { CorporateFormsService } from '../../services/corporate-forms.service';
import { CompaniesService } from '../../../services/companies.service';
import { UserService } from '../../../services/user.service';

import {
  ITaggedNoteListItem,
  INoteActivity,
  IConversationAttachment,
} from '../models/ITaggedNote.interface';
import { IMeetingNote, IInlineAttachment } from '../models/IMeetingNote.interface';
import { IReaction, IReactionSummary, REACTION_EMOJIS } from '../models/IReaction.interface';
import { CompanyUserDTO } from '../../../models/company/company-user-dto.model';
import { UserVM } from '../../../models/user/user-vm.model';
import { IFileStorageItem } from '../../../file-storage/models/IFileStoragePaginated.interface';
import { DMSFileCategory } from '../../../file-storage/enums/DMSFileCategory.enum';
import { RichTextEditorComponent } from '../rich-text-editor/rich-text-editor.component';
import { ReferenceNumberComponent } from '../../../shared/reference-number/reference-number.component';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
// DocumentViewerDialogComponent and FileStorageUploadDialogComponent are lazy-loaded on demand
// (see viewDocument / openUploadDialog) so the file-storage parsing cluster (mammoth, xlsx,
// pdf-lib, jszip, ...) stays out of the eager bundle even though this panel is always rendered.
import {
  AttachmentTypeDialogComponent, AttachmentType
} from '../attachment-type-dialog/attachment-type-dialog.component';

import { formatDistanceToNow, format } from 'date-fns';

type PanelTab = 'discussion' | 'auto_info' | 'documents' | 'details' | 'activity';
type FilterStatus = 'all' | 'active' | 'completed';

@Component({
  selector: 'app-meeting-notes-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    RichTextEditorComponent,
    ReferenceNumberComponent,
  ],
  templateUrl: './meeting-notes-panel.component.html',
  styleUrls: ['./meeting-notes-panel.component.scss'],
  animations: [
    trigger('slidePanel', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'none' })),
      ]),
      transition(':leave', [
        animate('250ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(100%)' })),
      ]),
    ]),
    trigger('fadeBackdrop', [
      state('void', style({ opacity: 0 })),
      state('*', style({ opacity: 1 })),
      transition(':enter', animate('200ms ease-out')),
      transition(':leave', animate('150ms ease-in')),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingNotesPanelComponent implements OnInit, OnDestroy {

  @ViewChild('chatContainer') chatContainer!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef;

  // Services
  public readonly panelService = inject(MeetingNotesPanelService);
  private readonly notesService = inject(MeetingNotesService);
  private readonly dmsService = inject(FileStorageService);
  private readonly timeSheetService = inject(TimeSheetService);
  private readonly formsService = inject(CorporateFormsService);
  private readonly companiesService = inject(CompaniesService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy = inject(DestroyRef);

  // State
  public isPanelOpen = false;
  public selectedNote: ITaggedNoteListItem | null = null;
  public taggedNotes: ITaggedNoteListItem[] = [];
  public isLoadingList = false;
  public isLoadingThread = false;
  public activeTab: PanelTab = 'discussion';
  public filterStatus: FilterStatus = 'all';
  public searchControl = new FormControl('');
  public messageControl = new FormControl('');
  public reactionEmojis = REACTION_EMOJIS;

  public tabsList = [
    { key: 'discussion' as PanelTab, label: 'Discussion', icon: 'chat_bubble_outline' },
    { key: 'auto_info' as PanelTab, label: 'Auto Info', icon: 'smart_toy' },
    { key: 'documents' as PanelTab, label: 'Documents', icon: 'description' },
    { key: 'details' as PanelTab, label: 'Details', icon: 'info_outline' },
    { key: 'activity' as PanelTab, label: 'Activity', icon: 'history' },
  ];

  // User data
  public currentUser!: UserVM;
  public users: CompanyUserDTO[] = [];
  public companies: any[] = [];

  // Thread data
  public noteThread: IMeetingNote | null = null;
  public allNotesFlat: IMeetingNote[] = [];
  public threadParticipantIds: number[] = [];
  public noteCompanyName: string = '';
  public dmsFiles: IFileStorageItem[] = [];
  public threadAttachments: IConversationAttachment[] = [];
  public activities: INoteActivity[] = [];

  // Reply state
  public noteToReply: IMeetingNote | null = null;
  public isReplyEdit = false;
  public mentionedUsers: CompanyUserDTO[] = [];
  private usersLoaded = false;

  // Reaction picker
  public activeReactionPickerNoteId: number | null = null;

  // Attachments
  public pendingFiles: File[] = [];

  // Timer state
  public isTimerRunning = false;
  public timerElapsedSeconds = 0;
  public timerBillable = true;
  public timerDescription = '';
  public showTimerDescription = false;
  private timerStartTime: Date | null = null;
  private timerInterval: any = null;
  private formCustomerInfo: { businessId?: number; individualId?: number; formType?: number } | null = null;

  // ============================================================
  // Lifecycle
  // ============================================================

  ngOnInit(): void {
    this.panelService.panelOpen$.pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(open => {
      this.isPanelOpen = open;
      if (open && !this.usersLoaded) {
        this.loadUserData();
      }
      this.cdr.markForCheck();
    });

    this.panelService.taggedNotes$.pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(notes => {
      this.taggedNotes = notes;
      this.cdr.markForCheck();
    });

    this.panelService.selectedNote$.pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(note => {
      this.selectedNote = note;
      if (note) {
        this.loadNoteThread(note);
        this.loadFormCustomerInfo(note.formId);
      } else {
        this.resetThreadState();
      }
      this.cdr.markForCheck();
    });

    this.panelService.loadingList$.pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(loading => {
      this.isLoadingList = loading;
      this.cdr.markForCheck();
    });

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroy)
    ).subscribe(search => {
      this.panelService.loadTaggedNotes({
        search: search || undefined,
        status: this.filterStatus === 'all' ? undefined : this.filterStatus,
      });
    });
  }

  ngOnDestroy(): void {
    this.panelService.closePanel();
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  // ============================================================
  // User Data
  // ============================================================

  private loadUserData(): void {
    forkJoin({
      currentUser: this.userService.getCurrentUser(false).pipe(
        catchError(err => { console.error('[MeetingNotesPanel] Error loading current user:', err); return of(null); })
      ),
      orgUsers: this.companiesService.getOrganizationUsers().pipe(
        catchError(err => { console.error('[MeetingNotesPanel] Error loading org users:', err); return of(null); })
      ),
      orgCompanies: this.companiesService.checkOrganizationCompanies().pipe(
        catchError(() => of(null))
      ),
    }).pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe((data: any) => {
      if (data.currentUser) {
        this.currentUser = data.currentUser;
      }
      if (data.orgUsers) {
        this.users = Array.isArray(data.orgUsers) ? data.orgUsers : data.orgUsers?.users || [];
        this.usersLoaded = true;
      }
      if (data.orgCompanies) {
        this.companies = Array.isArray(data.orgCompanies) ? data.orgCompanies : data.orgCompanies || [];
      }
      this.cdr.markForCheck();
    });

    // Load company name for Details tab
    this.loadCompanyName();
  }

  private loadCompanyName(): void {
    const companyId = parseInt(sessionStorage.getItem('companyId') || '0', 10);
    if (companyId > 0) {
      this.companiesService.getCompany(companyId).pipe(
        takeUntilDestroyed(this.destroy),
        catchError(() => of(null))
      ).subscribe((company: any) => {
        if (company) {
          this.noteCompanyName = company.name || company.companyName || '';
          this.cdr.markForCheck();
        }
      });
    }
  }

  // ============================================================
  // Panel Actions
  // ============================================================

  closePanel(): void {
    this.panelService.closePanel();
  }

  selectNote(note: ITaggedNoteListItem): void {
    this.activeTab = 'discussion';
    this.panelService.selectNote(note);
  }

  goBackToList(): void {
    this.panelService.deselectNote();
  }

  setActiveTab(tab: PanelTab): void {
    this.activeTab = tab;
    if (tab === 'documents' && this.selectedNote) {
      this.loadDocuments();
    }
    if (tab === 'activity' && this.selectedNote) {
      this.loadActivity();
    }
  }

  setFilter(status: string): void {
    this.filterStatus = status as FilterStatus;
    this.panelService.loadTaggedNotes({
      search: this.searchControl.value || undefined,
      status: status === 'all' ? undefined : status as any,
    });
  }

  openAllNotesPage(): void {
    window.open('/tagged-notes', '_blank');
  }

  openFullView(): void {
    if (!this.selectedNote) return;
    this.panelService.closePanel();
    this.router.navigate(['/tagged-notes'], {
      queryParams: {
        noteId: this.selectedNote.rootMeetingNoteId,
        formId: this.selectedNote.formId,
      }
    });
  }

  openFullViewNewTab(): void {
    if (!this.selectedNote) return;
    const params = new URLSearchParams({
      noteId: this.selectedNote.rootMeetingNoteId.toString(),
      formId: this.selectedNote.formId.toString(),
    });
    window.open(`/tagged-notes?${params.toString()}`, '_blank');
  }

  copyNoteUrl(): void {
    if (!this.selectedNote) return;
    const url = `${window.location.origin}/tagged-notes?noteId=${this.selectedNote.rootMeetingNoteId}&formId=${this.selectedNote.formId}`;
    navigator.clipboard.writeText(url).then(
      () => this.snackBar.open('Link copied to clipboard', 'Close', { duration: 2000 }),
      () => this.snackBar.open('Failed to copy link', 'Close', { duration: 3000 }),
    );
  }

  openReferencePage(note: ITaggedNoteListItem): void {
    if (note.referenceNumber) {
      window.open(`/reference-numbers?search=${encodeURIComponent(note.referenceNumber)}`, '_blank');
    }
  }

  openClientPage(note: ITaggedNoteListItem): void {
    if (note.formId) {
      window.open(`/edit-form/${note.formId}`, '_blank');
    }
  }

  // ============================================================
  // Thread Loading
  // ============================================================

  private loadNoteThread(note: ITaggedNoteListItem): void {
    this.isLoadingThread = true;
    this.cdr.markForCheck();

    this.panelService.getNoteThread(note.formId, note.rootMeetingNoteId).pipe(
      catchError(err => {
        console.error('Error loading note thread:', err);
        this.snackBar.open('Error loading conversation', 'Close', { duration: 3000 });
        return of(null);
      }),
      takeUntilDestroyed(this.destroy)
    ).subscribe(thread => {
      if (thread) {
        this.noteThread = thread;
        this.flattenNotes([thread]);
        this.loadReactionsForNotes(this.allNotesFlat);
        setTimeout(() => this.scrollToBottom(), 100);
      }
      this.isLoadingThread = false;
      this.cdr.markForCheck();
    });
  }

  private resetThreadState(): void {
    this.noteThread = null;
    this.allNotesFlat = [];
    this.dmsFiles = [];
    this.threadAttachments = [];
    this.activities = [];
    this.noteToReply = null;
    this.isReplyEdit = false;
    this.pendingFiles = [];
    this.mentionedUsers = [];
    this.activeReactionPickerNoteId = null;
  }

  // ============================================================
  // Discussion / Replies
  // ============================================================

  replyToNote(node: IMeetingNote): void {
    this.cancelReply();
    this.noteToReply = node;
    this.messageControl.setValue('');
  }

  editNote(node: IMeetingNote): void {
    this.cancelReply();
    this.isReplyEdit = true;
    this.noteToReply = node;
    this.messageControl.setValue(node.notes);
  }

  cancelReply(): void {
    this.noteToReply = null;
    this.isReplyEdit = false;
    this.messageControl.setValue('');
    this.pendingFiles = [];
    this.mentionedUsers = [];
  }

  submitMessage(): void {
    const notesValue = this.messageControl.value;
    if (!notesValue || notesValue.trim() === '') return;
    if (!this.selectedNote || !this.noteThread) return;

    if (this.isReplyEdit && this.noteToReply) {
      const noteToUpdate = {
        ...this.noteToReply,
        notes: notesValue,
        userID: this.currentUser.userId,
      };

      this.notesService.editNote(this.selectedNote.formId, noteToUpdate).pipe(
        takeUntilDestroyed(this.destroy)
      ).subscribe(() => {
        this.updateNoteInTree(this.noteToReply!.id!, notesValue);
        this.flattenNotes([this.noteThread!]);
        this.cancelReply();
        this.snackBar.open('Message updated', 'Close', { duration: 2000 });
        this.cdr.markForCheck();
      });
    } else {
      const parentId = this.noteToReply?.id || this.noteThread.id;

      const newNote: IMeetingNote = {
        referenceNumber: this.noteThread.referenceNumber,
        title: this.noteThread.title,
        year: this.noteThread.year,
        date: format(new Date(), 'yyyy-MM-dd'),
        departmentId: this.noteThread.departmentId,
        serviceId: this.noteThread.serviceId,
        userID: this.currentUser.userId,
        notes: notesValue,
        parentMeetingNoteId: parentId!,
      };

      this.notesService.createNote(this.selectedNote.formId, newNote).pipe(
        switchMap((id) => {
          newNote.id = id;
          newNote.replies = [];
          newNote.inlineAttachments = [];

          if (this.pendingFiles.length > 0) {
            return this.panelService.uploadInlineAttachments(
              this.selectedNote!.formId,
              id,
              this.pendingFiles,
            ).pipe(
              catchError(() => of([] as IInlineAttachment[])),
              switchMap((attachments) => {
                newNote.inlineAttachments = attachments;
                return of(id);
              })
            );
          }
          return of(id);
        }),
        takeUntilDestroyed(this.destroy)
      ).subscribe(() => {
        this.addReplyToTree(parentId!, newNote);
        this.flattenNotes([this.noteThread!]);
        this.cancelReply();
        this.snackBar.open('Message sent', 'Close', { duration: 2000 });
        this.cdr.markForCheck();
        setTimeout(() => this.scrollToBottom(), 100);
      });
    }
  }

  deleteNote(node: IMeetingNote): void {
    if (!this.selectedNote) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Message',
        text: 'Are you sure you want to delete this message?',
        subText: 'Delete',
      },
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;

      this.notesService.deleteNote(this.selectedNote!.formId, node.id!).pipe(
        takeUntilDestroyed(this.destroy)
      ).subscribe(() => {
        this.deleteNoteFromTree(node);
        this.flattenNotes([this.noteThread!]);
        this.snackBar.open('Message deleted', 'Close', { duration: 2000 });
        this.cdr.markForCheck();
      });
    });
  }

  onUserMentioned(user: CompanyUserDTO): void {
    if (!this.mentionedUsers.find(u => u.id === user.id)) {
      this.mentionedUsers.push(user);
    }
  }

  // ============================================================
  // Inline Attachments
  // ============================================================

  deleteInlineAttachment(note: IMeetingNote, attachment: IInlineAttachment): void {
    if (!this.selectedNote) return;

    this.panelService.deleteInlineAttachment(
      this.selectedNote.formId,
      note.id!,
      attachment.id
    ).pipe(
      takeUntilDestroyed(this.destroy),
      catchError(() => {
        this.snackBar.open('Error deleting attachment', 'Close', { duration: 3000 });
        return of(null);
      })
    ).subscribe(res => {
      if (res !== null) {
        note.inlineAttachments = (note.inlineAttachments || []).filter(a => a.id !== attachment.id);
        this.snackBar.open('Attachment deleted', 'Close', { duration: 2000 });
        this.cdr.markForCheck();
      }
    });
  }

  downloadInlineAttachment(attachment: IInlineAttachment): void {
    if (attachment.blobUrl) {
      const a = document.createElement('a');
      a.href = attachment.blobUrl;
      a.download = attachment.name;
      a.target = '_blank';
      a.click();
    }
  }

  isImageAttachment(attachment: IInlineAttachment): boolean {
    const ext = attachment.name?.split('.').pop()?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext || '');
  }

  // ============================================================
  // File Attachments
  // ============================================================

  triggerFileUpload(): void {
    this.fileInput?.nativeElement?.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files: File[] = Array.from(input.files);
      this.showAttachmentTypeChoice(files);
    }
    input.value = '';
  }

  private showAttachmentTypeChoice(files: File[]): void {
    const dialogRef = this.dialog.open(AttachmentTypeDialogComponent, {
      width: '420px',
      maxWidth: '96vw',
    });

    dialogRef.afterClosed().subscribe(async (result: AttachmentType | undefined) => {
      if (!result) return;

      if (result === 'screenshot') {
        this.pendingFiles = [...this.pendingFiles, ...files];
        this.cdr.markForCheck();
      } else if (result === 'company_document') {
        // Add files as inline attachments
        this.pendingFiles = [...this.pendingFiles, ...files];
        this.cdr.markForCheck();

        // Also open DMS upload dialog for proper categorization
        if (this.selectedNote && this.noteThread) {
          const {FileStorageUploadDialogComponent} = await import('../../../file-storage/components/file-storage-upload-dialog/file-storage-upload-dialog.component');
          const uploadDialogRef = FileStorageUploadDialogComponent.openDrawer(this.dialog, {
            submittedFormId: this.selectedNote.formId,
            acr: 'NOTE',
            code: 'MN',
            returnFile: true,
            description: 'Notes',
            client: this.selectedNote.clientName || '',
            referenceNumber: this.noteThread?.referenceNumber || this.selectedNote.referenceNumber || null,
            preSelectedFiles: files,
          });

          uploadDialogRef.afterClosed().pipe(
            switchMap(res => {
              if (!res || !res.file) return of(null);
              const uploadFiles = Array.isArray(res.file) ? res.file : [res];
              const uploads = uploadFiles.map((f: any) => {
                const entry = {
                  ...res,
                  category: DMSFileCategory.Notes,
                  sourceDocumentId: this.noteThread!.id!.toString(),
                  meetingNoteId: this.noteThread!.id,
                  client: this.selectedNote!.clientName || '',
                  file: f.file || f,
                };
                return this.dmsService.uploadFile(entry, this.selectedNote!.formId);
              });
              return forkJoin(uploads);
            })
          ).subscribe(result => {
            if (result) {
              const newFiles = Array.isArray(result) ? result : [result];
              this.dmsFiles = [...this.dmsFiles, ...newFiles];
              this.snackBar.open('Document uploaded to DMS', 'Close', { duration: 3000 });
              this.cdr.markForCheck();
            }
          });
        }
      }
    });
  }

  removePendingFile(index: number): void {
    this.pendingFiles.splice(index, 1);
    this.cdr.markForCheck();
  }

  async openUploadDialog(): Promise<void> {
    if (!this.selectedNote || !this.noteThread) return;

    const {FileStorageUploadDialogComponent} = await import('../../../file-storage/components/file-storage-upload-dialog/file-storage-upload-dialog.component');
    const dialogRef = FileStorageUploadDialogComponent.openDrawer(this.dialog, {
      submittedFormId: this.selectedNote.formId,
      acr: 'NOTE',
      code: 'MN',
      returnFile: true,
      description: 'Notes',
      client: this.selectedNote.clientName || '',
      referenceNumber: this.noteThread?.referenceNumber || this.selectedNote.referenceNumber || null,
    });

    dialogRef.afterClosed().pipe(
      switchMap(res => {
        if (!res || !res.file) return of(null);

        const files = Array.isArray(res.file) ? res.file : [res];
        const uploads = files.map((f: any) => {
          const entry = {
            ...res,
            category: DMSFileCategory.Notes,
            sourceDocumentId: this.noteThread!.id!.toString(),
            meetingNoteId: this.noteThread!.id,
            client: this.selectedNote!.clientName || '',
            file: f.file || f,
          };
          return this.dmsService.uploadFile(entry, this.selectedNote!.formId);
        });
        return forkJoin(uploads);
      })
    ).subscribe(result => {
      if (result) {
        const newFiles = Array.isArray(result) ? result : [result];
        this.dmsFiles = [...this.dmsFiles, ...newFiles];
        this.snackBar.open('File uploaded', 'Close', { duration: 2000 });
        this.cdr.markForCheck();
      }
    });
  }

  // ============================================================
  // Documents Tab
  // ============================================================

  private loadDocuments(): void {
    if (!this.selectedNote || !this.noteThread) return;

    const searchConfig: any = {
      submittedFormId: this.selectedNote.formId,
      category: DMSFileCategory.Notes,
    };

    this.dmsService.search(searchConfig, { pageNumber: 1, pageSize: 10000 }).pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(files => {
      this.dmsFiles = files.items.filter(
        f => f.sourceDocumentId === this.noteThread!.id!.toString()
      );
      this.cdr.markForCheck();
    });

    // Build conversation attachments from the loaded thread notes
    this.threadAttachments = this.allNotesFlat.reduce((acc, note) => {
      if (note.inlineAttachments?.length) {
        for (const att of note.inlineAttachments) {
          acc.push({
            id: att.id,
            meetingNoteId: note.id!,
            fileName: att.name,
            fileSize: att.fileSize,
            fileType: att.contentType || '',
            uploadedByUserId: note.userID!,
            uploadedDate: att.uploadedAt,
            downloadUrl: att.blobUrl,
          });
        }
      }
      return acc;
    }, [] as IConversationAttachment[]);
    this.cdr.markForCheck();
  }

  async viewDocument(doc: IFileStorageItem): Promise<void> {
    const {DocumentViewerDialogComponent} = await import('../../../file-storage/components/document-viewer-dialog/document-viewer-dialog.component');
    this.dialog.open(DocumentViewerDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '85vh',
      panelClass: 'document-viewer-dialog',
      data: { document: doc },
    });
  }

  downloadDocument(doc: IFileStorageItem): void {
    const a = document.createElement('a');
    a.href = doc.downloadUrl;
    a.download = doc.generatedFileName;
    a.target = '_blank';
    a.click();
  }

  downloadConversationAttachment(att: IConversationAttachment): void {
    if (att.downloadUrl) {
      const a = document.createElement('a');
      a.href = att.downloadUrl;
      a.download = att.fileName || 'attachment';
      a.target = '_blank';
      a.click();
    }
  }

  // ============================================================
  // Activity Tab
  // ============================================================

  private loadActivity(): void {
    if (!this.selectedNote) return;

    this.panelService.getActivity(this.selectedNote.rootMeetingNoteId).pipe(
      catchError(() => of({ items: [], totalCount: 0, totalPages: 0 })),
      takeUntilDestroyed(this.destroy)
    ).subscribe(res => {
      this.activities = (res.items || []).map((item: any) => this.mapActivity(item));
      this.cdr.markForCheck();
    });
  }

  private mapActivity(item: any): INoteActivity {
    const type = item.type || (item.parentMeetingNoteId ? 'reply' : 'created');
    const rawDetail = item.detail || item.notes || '';
    return {
      id: item.id || item.noteId,
      type,
      userId: item.userId,
      timestamp: item.timestamp || item.date,
      detail: this.stripHtml(rawDetail),
      metadata: item.metadata,
    };
  }

  // ============================================================
  // Reactions
  // ============================================================

  toggleReactionPicker(node: IMeetingNote): void {
    this.activeReactionPickerNoteId =
      this.activeReactionPickerNoteId === node.id ? null : node.id!;
    this.cdr.markForCheck();
  }

  addReaction(node: IMeetingNote, emojiItem: { emoji: string; code: string; label: string }): void {
    if (!this.selectedNote) return;
    this.activeReactionPickerNoteId = null;

    const existing = node.reactionSummary?.find(
      r => r.emoji === emojiItem.emoji && r.hasCurrentUserReacted
    );
    if (existing) {
      this.removeReaction(node, emojiItem.emoji);
      return;
    }

    const noteOwner = this.users?.find(u => u.azureObjectId === this.currentUser?.azureObjectId);
    const request = {
      emoji: emojiItem.emoji,
      noteOwnerAzureObjectId: noteOwner?.azureObjectId || '',
      clientName: this.selectedNote.clientName,
      noteTitle: this.noteThread?.title || node.title,
      rootMeetingNoteId: this.selectedNote.rootMeetingNoteId,
    };

    this.notesService.addReaction(this.selectedNote.formId, node.id!, request).pipe(
      takeUntilDestroyed(this.destroy),
      catchError(() => of(null))
    ).subscribe(reaction => {
      if (reaction) {
        if (!node.reactions) node.reactions = [];
        node.reactions.push(reaction);
        this.updateNodeReactionSummary(node, emojiItem.emoji, true, reaction.id);
        this.cdr.markForCheck();
      }
    });
  }

  removeReaction(node: IMeetingNote, emoji: string): void {
    if (!this.selectedNote) return;

    const userReaction = node.reactions?.find(
      r => r.userAzureObjectId === this.currentUser?.azureObjectId && r.emoji === emoji
    );
    const reactionId = userReaction?.id ??
      node.reactionSummary?.find(r => r.emoji === emoji && r.hasCurrentUserReacted)?.currentUserReactionId;

    if (!reactionId) return;

    this.notesService.removeReaction(this.selectedNote.formId, node.id!, reactionId).pipe(
      takeUntilDestroyed(this.destroy),
      catchError(() => of(null))
    ).subscribe(() => {
      if (node.reactions) {
        node.reactions = node.reactions.filter(r => r.id !== reactionId);
      }
      this.updateNodeReactionSummary(node, emoji, false);
      this.cdr.markForCheck();
    });
  }

  toggleReaction(node: IMeetingNote, emoji: string): void {
    const reaction = node.reactionSummary?.find(r => r.emoji === emoji);
    if (reaction?.hasCurrentUserReacted) {
      this.removeReaction(node, emoji);
    } else {
      const emojiItem = this.reactionEmojis.find(e => e.emoji === emoji) || { emoji, code: '', label: '' };
      this.addReaction(node, emojiItem);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.reaction-picker-container')) {
      this.activeReactionPickerNoteId = null;
    }
  }

  // ============================================================
  // Helpers
  // ============================================================

  getInitials(userId: number | null | undefined): string {
    if (userId === 0) return 'SYS';
    if (!userId) return 'U';
    const user = this.users?.find(u => u.id === userId);
    if (user) {
      return `${(user.firstName || '').charAt(0)}${(user.lastName || '').charAt(0)}`.toUpperCase();
    }
    return 'U';
  }

  getUserName(userId: number | null | undefined): string {
    if (userId === 0) return 'System';
    if (userId === null || userId === undefined) return 'Unknown';
    const user = this.users?.find(u => u.id === userId);
    if (user) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown';
    }
    return 'Unknown';
  }

  isOwnMessage(userId: number | null | undefined): boolean {
    return userId === this.currentUser?.userId;
  }

  getCompanyName(companyId: number | undefined): string {
    if (!companyId) return '';
    const company = this.companies?.find((c: any) => c.id === companyId);
    return company?.name || '';
  }

  getParentAuthorName(parentNoteId: number | null | undefined): string {
    if (!parentNoteId) return 'Unknown';
    const parentNote = this.findNoteById(parentNoteId);
    if (parentNote) {
      return this.getUserName(parentNote.userID);
    }
    return 'Unknown';
  }

  getReactionTooltip(reaction: IReactionSummary): string {
    if (!reaction.userAzureObjectIds?.length) return reaction.emoji;
    const names = reaction.userAzureObjectIds.slice(0, 5).map(azureId => {
      const user = this.users?.find(u => u.azureObjectId === azureId);
      return user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Unknown';
    });
    if (reaction.userAzureObjectIds.length > 5) {
      names.push(`and ${reaction.userAzureObjectIds.length - 5} more`);
    }
    return names.join(', ');
  }

  formatTimeAgo(dateStr: string): string {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return dateStr;
    }
  }

  formatDate(dateStr: string | number): string {
    try {
      return format(new Date(dateStr), 'dd MMM yyyy, HH:mm');
    } catch {
      return String(dateStr);
    }
  }

  getFileIcon(fileName: string): string {
    if (!fileName) return 'insert_drive_file';
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'picture_as_pdf';
      case 'doc': case 'docx': return 'description';
      case 'xls': case 'xlsx': return 'table_chart';
      case 'zip': case 'rar': return 'folder_zip';
      case 'png': case 'jpg': case 'jpeg': case 'gif': return 'image';
      default: return 'insert_drive_file';
    }
  }

  getFileIconClass(fileName: string): string {
    if (!fileName) return 'default';
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'pdf';
      case 'doc': case 'docx': return 'doc';
      case 'xls': case 'xlsx': return 'xls';
      default: return 'default';
    }
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  getActivityIcon(type: string): string {
    switch (type) {
      case 'reply': return 'chat_bubble_outline';
      case 'upload': return 'attach_file';
      case 'reaction': return 'emoji_emotions';
      case 'status_change': return 'swap_horiz';
      case 'created': return 'add_circle_outline';
      case 'mention': return 'alternate_email';
      case 'edit': return 'edit';
      default: return 'info';
    }
  }

  titleHasYear(note: any): boolean {
    return note?.year && note?.title?.includes(String(note.year));
  }

  stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  }

  isSystemMessage(note: IMeetingNote): boolean {
    return note.userID === 0 || note.userID === null || note.userID === undefined;
  }

  get discussionNotes(): IMeetingNote[] {
    return this.allNotesFlat.filter(n => !this.isSystemMessage(n));
  }

  get systemNotes(): IMeetingNote[] {
    return this.allNotesFlat.filter(n => this.isSystemMessage(n));
  }

  trackByNoteId(index: number, item: IMeetingNote): number | undefined {
    return item.id;
  }

  trackByListItem(index: number, item: ITaggedNoteListItem): number {
    return item.noteId;
  }

  // ============================================================
  // Timer
  // ============================================================

  private loadFormCustomerInfo(formId: number): void {
    if (!formId) return;
    this.formsService.getFormToEdit(1, formId).pipe(
      catchError(() => of(null)),
      takeUntilDestroyed(this.destroy)
    ).subscribe((formData: any) => {
      if (formData) {
        this.formCustomerInfo = {
          businessId: formData.businessId,
          individualId: formData.individualId,
          formType: formData.formType,
        };
      }
    });
  }

  startTimer(): void {
    if (this.isTimerRunning) return;
    this.isTimerRunning = true;
    this.timerStartTime = new Date();
    this.timerElapsedSeconds = 0;
    this.timerDescription = '';
    this.showTimerDescription = false;

    this.timerInterval = setInterval(() => {
      if (this.timerStartTime) {
        this.timerElapsedSeconds = Math.floor((Date.now() - this.timerStartTime.getTime()) / 1000);
        this.cdr.markForCheck();
      }
    }, 1000);
  }

  stopTimer(): void {
    if (!this.isTimerRunning) return;

    if (!this.showTimerDescription) {
      this.showTimerDescription = true;
      this.timerDescription = '';
      this.cdr.markForCheck();
      return;
    }

    this.isTimerRunning = false;
    clearInterval(this.timerInterval);
    this.timerInterval = null;
    this.showTimerDescription = false;

    const endTime = new Date();

    if (!this.formCustomerInfo || (!this.formCustomerInfo.businessId && !this.formCustomerInfo.individualId)) {
      this.snackBar.open('Cannot create timesheet: Customer information not available', 'Close', { duration: 5000 });
      this.resetTimer();
      return;
    }

    const departmentId = this.noteThread?.departmentId || this.selectedNote?.departmentId;
    const serviceId = this.noteThread?.serviceId || this.selectedNote?.serviceId;

    if (!departmentId || !serviceId) {
      this.snackBar.open('Department and Service are required for timesheet', 'Close', { duration: 5000 });
      this.resetTimer();
      return;
    }

    this.createTimesheetEntry(this.timerStartTime!, endTime, departmentId, serviceId);
  }

  cancelTimer(): void {
    this.isTimerRunning = false;
    clearInterval(this.timerInterval);
    this.timerInterval = null;
    this.showTimerDescription = false;
    this.resetTimer();
    this.cdr.markForCheck();
  }

  private createTimesheetEntry(startTime: Date, endTime: Date, departmentId: number, serviceId: number): void {
    const meetingNoteUrl = this.generateTimerMeetingNoteUrl();
    const userComment = this.timerDescription?.trim() || '';
    const autoComment = `Meeting Note: ${this.noteThread?.referenceNumber || ''} | ${meetingNoteUrl}`;
    const comments = userComment ? `${userComment} | ${autoComment}` : autoComment;

    const timeSheetRequest: ITimeSheetRequest = {
      date: formatDate(startTime, 'yyyy-MM-dd', 'en-US'),
      serviceId: serviceId,
      departmentId: departmentId,
      individualId: this.formCustomerInfo?.individualId || null,
      businessId: this.formCustomerInfo?.businessId || null,
      organizationCompanyId: null,
      description: this.noteThread?.title || 'Meeting Note',
      startTime: this.formatTimeForApi(startTime),
      endTime: this.formatTimeForApi(endTime),
      billable: this.timerBillable,
      comments: comments,
      year: new Date().getFullYear(),
      attendees: [],
    } as any;

    this.timeSheetService.createTimeSheet(timeSheetRequest).pipe(
      takeUntilDestroyed(this.destroy),
      catchError((err) => {
        this.snackBar.open('Error creating timesheet entry', 'Close', { duration: 5000 });
        throw err;
      })
    ).subscribe(() => {
      this.snackBar.open(`Timesheet entry created: ${this.formatElapsedTime(this.timerElapsedSeconds)}`, 'Close', { duration: 5000 });
      this.resetTimer();
      this.cdr.markForCheck();
    });
  }

  private generateTimerMeetingNoteUrl(): string {
    if (!this.selectedNote) return '';
    const baseUrl = window.location.origin;
    const noteUrl = this.router.createUrlTree(['/tagged-notes'], {
      queryParams: {
        noteId: this.selectedNote.rootMeetingNoteId,
        formId: this.selectedNote.formId,
      }
    });
    return `${baseUrl}${this.router.serializeUrl(noteUrl)}`;
  }

  private resetTimer(): void {
    this.timerElapsedSeconds = 0;
    this.timerStartTime = null;
    this.timerDescription = '';
  }

  private formatTimeForApi(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  formatElapsedTime(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  // ============================================================
  // Tree Helpers
  // ============================================================

  private flattenNotes(notes: IMeetingNote[]): void {
    this.allNotesFlat = [];
    this.flattenNotesInto(notes, this.allNotesFlat);

    // Compute unique participant IDs from the thread
    const userIds = new Set<number>();
    for (const note of this.allNotesFlat) {
      if (note.userID !== null && note.userID !== undefined) {
        userIds.add(note.userID);
      }
    }
    this.threadParticipantIds = Array.from(userIds).filter(id => id !== 0);
  }

  private flattenNotesInto(notes: IMeetingNote[], result: IMeetingNote[]): void {
    for (const note of notes) {
      result.push(note);
      if (note.replies?.length) {
        this.flattenNotesInto(note.replies, result);
      }
    }
  }

  private findNoteById(noteId: number): IMeetingNote | null {
    const find = (notes: IMeetingNote[]): IMeetingNote | null => {
      for (const note of notes) {
        if (note.id === noteId) return note;
        if (note.replies?.length) {
          const found = find(note.replies);
          if (found) return found;
        }
      }
      return null;
    };
    return this.noteThread ? find([this.noteThread]) : null;
  }

  private updateNoteInTree(noteId: number, newNotes: string): void {
    const update = (notes: IMeetingNote[]): boolean => {
      for (const note of notes) {
        if (note.id === noteId) { note.notes = newNotes; return true; }
        if (note.replies?.length && update(note.replies)) return true;
      }
      return false;
    };
    if (this.noteThread) update([this.noteThread]);
  }

  private addReplyToTree(parentId: number, newReply: IMeetingNote): void {
    const add = (notes: IMeetingNote[]): boolean => {
      for (const note of notes) {
        if (note.id === parentId) {
          if (!note.replies) note.replies = [];
          note.replies.push(newReply);
          return true;
        }
        if (note.replies?.length && add(note.replies)) return true;
      }
      return false;
    };
    if (this.noteThread) add([this.noteThread]);
  }

  private deleteNoteFromTree(noteToDelete: IMeetingNote): void {
    const del = (notes: IMeetingNote[]): boolean => {
      for (let i = 0; i < notes.length; i++) {
        if (notes[i].id === noteToDelete.id) { notes.splice(i, 1); return true; }
        if (notes[i].replies?.length && del(notes[i].replies!)) return true;
      }
      return false;
    };
    if (this.noteThread?.replies) del(this.noteThread.replies);
  }

  private updateNodeReactionSummary(
    node: IMeetingNote, emoji: string, isAdding: boolean, reactionId?: number
  ): void {
    if (!node.reactionSummary) node.reactionSummary = [];

    const idx = node.reactionSummary.findIndex(r => r.emoji === emoji);

    if (isAdding) {
      if (idx >= 0) {
        node.reactionSummary[idx].count++;
        node.reactionSummary[idx].hasCurrentUserReacted = true;
        node.reactionSummary[idx].currentUserReactionId = reactionId;
        if (!node.reactionSummary[idx].userAzureObjectIds.includes(this.currentUser.azureObjectId)) {
          node.reactionSummary[idx].userAzureObjectIds.push(this.currentUser.azureObjectId);
        }
      } else {
        node.reactionSummary.push({
          emoji, count: 1,
          userAzureObjectIds: [this.currentUser.azureObjectId],
          hasCurrentUserReacted: true,
          currentUserReactionId: reactionId,
        });
      }
    } else {
      if (idx >= 0) {
        node.reactionSummary[idx].count--;
        node.reactionSummary[idx].hasCurrentUserReacted = false;
        node.reactionSummary[idx].currentUserReactionId = undefined;
        node.reactionSummary[idx].userAzureObjectIds = node.reactionSummary[idx].userAzureObjectIds.filter(
          id => id !== this.currentUser.azureObjectId
        );
        if (node.reactionSummary[idx].count <= 0) {
          node.reactionSummary.splice(idx, 1);
        }
      }
    }
    node.reactionSummary = [...node.reactionSummary];
  }

  private loadReactionsForNotes(notes: IMeetingNote[]): void {
    if (!this.selectedNote) return;
    const withIds = notes.filter(n => n.id);
    if (!withIds.length) return;

    const requests = withIds.map(note =>
      this.notesService.getReactions(this.selectedNote!.formId, note.id!).pipe(
        catchError(() => of([] as IReaction[]))
      )
    );

    forkJoin(requests).pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(allReactions => {
      withIds.forEach((note, i) => {
        note.reactions = allReactions[i];
        note.reactionSummary = this.buildReactionSummary(allReactions[i]);
      });
      this.cdr.markForCheck();
    });
  }

  private buildReactionSummary(reactions: IReaction[]): IReactionSummary[] {
    if (!reactions?.length) return [];
    const map = new Map<string, IReactionSummary>();

    for (const r of reactions) {
      const existing = map.get(r.emoji);
      if (existing) {
        existing.count++;
        existing.userAzureObjectIds.push(r.userAzureObjectId);
        if (r.userAzureObjectId === this.currentUser?.azureObjectId) {
          existing.hasCurrentUserReacted = true;
          existing.currentUserReactionId = r.id;
        }
      } else {
        map.set(r.emoji, {
          emoji: r.emoji, count: 1,
          userAzureObjectIds: [r.userAzureObjectId],
          hasCurrentUserReacted: r.userAzureObjectId === this.currentUser?.azureObjectId,
          currentUserReactionId: r.userAzureObjectId === this.currentUser?.azureObjectId ? r.id : undefined,
        });
      }
    }
    return Array.from(map.values());
  }

  private scrollToBottom(): void {
    try {
      if (this.chatContainer) {
        this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
      }
    } catch {}
  }
}
